import React, { useCallback, useEffect, useState } from "react";

import { getFarmDetails, getField, getRedzones } from "services";

import {
  canDeleteField,
  formatNumber,
  matchGeoFirstAndLastCoords,
  scrollPage,
  useAddField,
  useAppType,
  useCancelAsyncOnUnmount,
  useDeleteField,
  useGeneralContext,
  useGetAllCarbonProgramOutcomes,
  useGetCarbonProgramByFarmId,
  useGetFarmReportingYear,
  useGetFieldShape,
  useGetVerificationEditingRequests,
  useTimelineModal,
  useTranslation,
  useUpdateField,
  useUpdateVerificationEditingRequest,
  useUserContext,
} from "utils";

import {
  AddFieldRequestBody,
  Coordinates,
  EDITING_REQUEST_TYPE,
  FarmDetailsResponse,
  FieldDetails as FieldDetailsResponse,
  FieldShape,
  RedzoneResponse,
} from "models";

import {
  Anchor,
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Form,
  Icon,
  IllustrationCard,
  Input,
  KlimRadioGroup,
  MapStatic,
  PageSpin,
  Row,
  StickyFooterButtons,
  Svg,
  Message as message,
} from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";
import { FieldLocation } from "pages/app/fields/components/FieldLocation";
import { SplitFieldDrawer } from "pages/app/fields/components/SplitFieldDrawer";

type FieldDetailsProps = {
  fieldId?: string;
  review?: boolean;
  onReviewFinish?: () => void;
  setShowFieldEditDrawer?: (show: boolean) => void;
  setShowFieldSplittingDrawer?: (show: boolean) => void;
};

type FieldFormValues = {
  name: string;
  isInRedzone?: boolean;
  nearby?: string[];
  editingRequestComment?: { [key: string]: string };
};

export const FieldDetails = ({ fieldId, review, onReviewFinish, setShowFieldEditDrawer }: FieldDetailsProps) => {
  const { t, ready } = useTranslation(["onboarding", "enums"], { useSuspense: false });
  const { user } = useUserContext();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(user?.farm.id || undefined);
  const { data: carbonProgramOutcomes } = useGetAllCarbonProgramOutcomes(user?.farm.id || undefined, carbonProgram);
  const { data: activeFarmReportingYear } = useGetFarmReportingYear(user?.farm.id || null);
  const { isSubmissionFeatureEnabled, isSubmissionPeriod, isDataSubmitted } = useSubmissionStatus(activeFarmReportingYear);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [field, setField] = useState<FieldDetailsResponse | null>(null);
  const [location, setLocation] = useState<FieldDetailsResponse["location"] | null>(null);
  const [crops, setCrops] = useState<FieldDetailsResponse["crops"]>([]);
  const [nearby, setNearby] = useState<FieldDetailsResponse["nearby"]>([]);
  const [farm, setFarm] = useState<FarmDetailsResponse | null>(null);
  const [showMapDrawer, setShowMapDrawer] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [showPrivacyDrawer, setShowPrivacyDrawer] = useState(false);
  const [showFieldSplitDrawer, setShowFieldSplitDrawer] = useState(false);
  const [fieldShape, setFieldShape] = useState<FieldShape | null>(null);
  const [fieldSize, setFieldSize] = useState<FieldDetailsResponse["size"] | null>(null);
  const [nameError, setNameError] = useState(false);
  const [redzone, setRedzone] = useState<RedzoneResponse | null>(null);

  const [form] = Form.useForm<FieldFormValues>();
  const isInRedzone = Form.useWatch("isInRedzone", form);

  const signal = useCancelAsyncOnUnmount()();
  const appType = useAppType();
  // we need the high quality version of the shape, otherwise the size calculated from the shape will be different from the field size we saved in the db
  const { fieldShape: fieldShapeResponse, fieldShapeLoading } = useGetFieldShape({ fieldId: fieldId || "", highQuality: true });
  const { mutate: updateField } = useUpdateField({ farmId: user?.farm.id || "" });
  const { mutate: addField } = useAddField();
  const { mutateAsync: deleteFieldAsync } = useDeleteField();
  const { showModal, hideModal } = useTimelineModal();
  const { setNavigationCrumbs } = useGeneralContext();
  const { data: editingRequests } = useGetVerificationEditingRequests({ farmId: user?.farm.id || "" });
  const { mutateAsync: submitEditingRequestComment } = useUpdateVerificationEditingRequest({ farmId: user?.farm.id || "" });

  const hasFieldSplitEditingRequest = editingRequests?.some(
    (request) => request.fieldId === fieldId && request.requestType === EDITING_REQUEST_TYPE.FIELD_SPLIT && !request.completedAt
  );
  const showFieldSplitButton = hasFieldSplitEditingRequest ? true : !(isSubmissionPeriod && isDataSubmitted);

  const loadField = useCallback(() => {
    if (!fieldId) {
      setLoading(false);
      return;
    }

    getField({
      id: fieldId || "",
      signal,
    })
      .then((res) => {
        setField(res);
        setFieldSize(res.size);
        setCrops(res.crops);
        setNearby(res.nearby);
        setLocation(res.location || location);

        if (!res.name || !res.name.length) {
          setNameError(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fieldId, location, signal]);

  const loadFarmDetails = useCallback(() => {
    getFarmDetails({ farmId: user?.farm.id || "" }).then((res) => {
      setFarm(res);
    });
  }, [user?.farm.id]);

  // Whenever the geometry changes we want to check if it is in a redzone
  const loadRedzone = useCallback(() => {
    if (!fieldShape || !farm) {
      return;
    }

    getRedzones({
      ...fieldShape.center,
      // state: farm.region,
      includeGeometry: true,
      signal,
    }).then((res) => {
      // For now only one redzone can be found
      setRedzone(res[0] || null);
    });
  }, [fieldShape, farm, signal]);

  const submitEditingRequestComments = async (formValues: FieldFormValues) => {
    const comments = formValues?.editingRequestComment || {};

    const commentSubmissions = Object.entries(comments)
      .filter(([_, comment]) => comment && typeof comment === "string" && comment.trim())
      .map(async ([type, comment]) => {
        const request = editingRequests?.find((r) => r.fieldId === fieldId && r.requestType === type && r.completedAt === null);
        if (request) {
          await submitEditingRequestComment({
            requestId: request.id,
            userComment: comment,
          });
        }
      });

    if (commentSubmissions.length > 0) {
      await Promise.allSettled(commentSubmissions);
    }
  };

  const complete = async (v: FieldFormValues) => {
    if (!v.name) {
      message({ content: t("errors.mandatoryFields"), icon: "error" });
      return setNameError(true);
    }
    if (!fieldShape || !location || !fieldSize) {
      message({ content: t("fieldDetails.errors.shape"), icon: "error" });
      return setLocationError(true);
    }

    // If redzone is accurate we confirm the redzone, otherwise it depends on the form selection
    const assignedRedzone: AddFieldRequestBody["redzone"] = redzone
      ? {
          id: redzone.id,
          state: redzone.state,
          accurate: redzone.accurate,
          confirmed: !!isInRedzone,
          geometry: {
            center: fieldShape.center,
            geometry: redzone.geometry,
            type: fieldShape.type,
          },
        }
      : null;

    if (fieldId) {
      setSaving(true);

      updateField(
        {
          ...v,
          ...{
            location: location,
            crops: crops,
            id: fieldId,
            nearby: nearby,
            geometry: {
              center: fieldShape.center,
              geometry: fieldShape.geometry,
              type: fieldShape.type,
            },
            size: fieldSize,
            redzone: assignedRedzone,
            deleteRedzone: !assignedRedzone,
            signal,
          },
        },
        {
          onSuccess: async () => {
            await submitEditingRequestComments(v);

            setNavigationCrumbs((prev) => ({
              ...prev,
              crumbs: prev.crumbs.map((crumb, index) => (index === 3 ? { ...crumb, name: v.name } : crumb)),
            }));

            message({ content: t("fieldSaved"), icon: "tick" });
            if (onReviewFinish) {
              onReviewFinish();
            } else {
              setShowFieldEditDrawer?.(false);
            }
          },
          onSettled: () => {
            setSaving(false);
          },
        }
      );
    } else {
      setSaving(true);

      addField(
        {
          name: v.name,
          location: location,
          crops: crops,
          nearby: nearby,
          geometry: {
            center: fieldShape.center,
            geometry: fieldShape.geometry,
            type: fieldShape.type,
          },
          size: fieldSize,
          redzone: assignedRedzone,
          signal,
        },
        {
          onSuccess: () => {
            message({ content: t("fieldCreated"), icon: "tick" });
            if (setShowFieldEditDrawer) {
              setShowFieldEditDrawer(false);
              return;
            }
            setLocation(null);
            setFieldShape(null);
            scrollPage();
          },
          onSettled: () => {
            setLoading(false);
            setSaving(false);
          },
        }
      );
    }
  };

  const incomplete = (errorInfo: { values: FieldFormValues }) => {
    if (!errorInfo.values.name) {
      message({ content: t("fieldDetails.errors.name"), icon: "error" });
      if (!fieldShape) {
        setLocationError(true);
      }
      return setNameError(true);
    }
    if (!fieldShape && (!location || !(location.lat && location.lng))) {
      message({ content: t("fieldDetails.errors.shape"), icon: "error" });
      return setLocationError(true);
    }
  };

  const setCurrentLocation = (location: Coordinates | null) => {
    if (!location) return;

    setLocationError(false);
    setLocation({
      lat: location.lat,
      lng: location.lng,
    });
  };

  const handleMapOpen = () => {
    setShowMapDrawer(true);
  };

  const handlePrivacyDrawer = () => {
    setShowPrivacyDrawer(!showPrivacyDrawer);
  };

  const showLocationMessage = () => {
    message({ content: t("fieldDetails.fieldSizeMessage"), icon: "tick" });
  };

  useEffect(() => {
    if (fieldShapeResponse) {
      setFieldShape({ ...fieldShapeResponse, geometry: matchGeoFirstAndLastCoords(fieldShapeResponse.geometry) });
      setLoading(false);
    }
  }, [fieldShapeResponse]);

  useEffect(() => {
    // Set loading to true here as well, otherwise if this component is used in the drawer
    // the loading state will not be reset when switching between fields
    setLoading(true);
    if (fieldId) {
      loadField();
    } else {
      setLoading(false);
    }
  }, [farm, fieldId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fieldId && !fieldShape) {
      setLocationError(true);
    }
  }, [fieldShape, fieldId]);

  useEffect(() => {
    loadFarmDetails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever the geometry changes we want to check if it is in a redzone
  useEffect(() => {
    loadRedzone();
  }, [loadRedzone]);

  const onModalSubmit = useCallback(
    async (fieldId: string) => {
      await deleteFieldAsync({
        fieldId,
      });
      hideModal();
    },
    [deleteFieldAsync, hideModal]
  );

  const handleDeleteConfirmation = useCallback(() => {
    if (field) {
      showModal({
        title: t("farm:deleteModalConfirm.fieldTitle", { fieldName: field.name }),
        message: t("farm:deleteModalConfirm.fieldMessage", { fieldName: field.name }),
        confirmText: t("farm:deleteModalConfirm.deleteText"),
        cancelText: t("farm:deleteModalConfirm.cancel"),
        onConfirm: () => onModalSubmit(field.id),
      });
    }
  }, [field, onModalSubmit, t, showModal]);

  return ready ? (
    <section className="onboarding">
      {showPrivacyDrawer && (
        <Drawer title={t("privacy.headline")} visible={showPrivacyDrawer} onClose={() => handlePrivacyDrawer()} className="privacy-drawer">
          <IllustrationCard>
            <Svg svgName="misc/data_privacy" />
          </IllustrationCard>
          <h2>{t("privacy.title")}</h2>
          <p>{t("privacy.description")}</p>
          <Anchor href="/privacy-policy" target="_blank">
            {t("privacy.link")}
          </Anchor>
        </Drawer>
      )}
      <section className={appType.mobile ? "tiny" : "tiny container"}>
        <Row>
          <Col>
            {fieldId ? (
              review ? (
                <h1>{t("fieldDetails.headlineReview")}</h1>
              ) : (
                <h1>{t("fieldDetails.headlineUpdate")}</h1>
              )
            ) : (
              <h1>{t("fieldDetails.headline")}</h1>
            )}
          </Col>
        </Row>
        {fieldId && (
          <Row>
            <Col>
              <p style={{ maxWidth: "768px" }}>{review ? t("fieldDetails.descriptionReview") : t("fieldDetails.descriptionUpdate")}</p>
            </Col>
          </Row>
        )}
        {!showFieldSplitButton && (
          <Row>
            <Col>
              <Flex gap="0.5rem" align="flex-start" className="field-details-split-field-not-allowed-banner">
                <Icon iconName="tabler/alert-circle" className="icon inline" />
                <p className="paragraph-m margin-0">{t("fieldDetails.fieldSplittingNotAllowed")}</p>
              </Flex>
            </Col>
          </Row>
        )}
        {!loading && !fieldShapeLoading ? (
          <Form form={form} name="field" onFinish={(v: FieldFormValues) => complete(v)} onFinishFailed={incomplete}>
            {isSubmissionFeatureEnabled &&
              [EDITING_REQUEST_TYPE.FIELD_SHAPE, EDITING_REQUEST_TYPE.FIELD_SPLIT, EDITING_REQUEST_TYPE.FIELD_REDZONE].map((type) => (
                <EditingRequestBanner key={type} type={type} farmId={farm?.id || ""} id={fieldId || ""} />
              ))}
            <section>
              {fieldShape && fieldShape.center && fieldShape.type !== "googlePolygon" && fieldShape.type !== "digifarm" ? (
                <>
                  <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]}>
                    <Col>
                      <Card className="map-card">
                        {/* @ts-expect-error need to convert to TS */}
                        <MapStatic field={field} geometry={fieldShape} marker />
                        {showFieldSplitButton && (
                          <div className="split-icon" data-testid="split-icon">
                            <Icon
                              iconName="tabler/slice"
                              className="icon inline"
                              onClick={() => {
                                setShowFieldSplitDrawer?.(true);
                              }}
                            />
                          </div>
                        )}
                        <div className="edit-icon">
                          <Icon iconName="klim/edit-element" className="icon inline" onClick={() => handleMapOpen()} />
                        </div>
                      </Card>
                      <p className="padding-top-100">
                        <Icon iconName="klim/field-size" className="icon inline" />
                        {`${formatNumber({ number: fieldSize ?? field?.size ?? 0, decimalPlaces: 2 })} ha`}
                        {(redzone?.accurate || isInRedzone) && (
                          <>
                            <Icon iconName="klim/fertiliser-strikethrough" className="icon inline margin-left-100" /> {t("fieldDetails.redzone")}
                          </>
                        )}
                      </p>
                    </Col>
                  </Row>
                  {redzone && (
                    <Row>
                      <Col>
                        <p className="margin-top-0 margin-bottom-50">{t("fieldDetails.isInRedzone")}</p>
                        <Form.Item name="isInRedzone" initialValue={fieldId ? field?.isInRedzone : redzone.accurate}>
                          <KlimRadioGroup
                            options={[
                              { label: t("yes"), value: true },
                              { label: t("no"), value: false },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                            type="tertiary"
                            size="large"
                            fullWidth
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                <>
                  <p>{t("fieldDetails.setSizeExplainer")}</p>
                  <Card className={`field-size-card ${locationError ? "location-placeholder error" : "location-placeholder"}`} onClick={() => handleMapOpen()}>
                    <Icon color={locationError ? "#db3804" : "#065c45"} dataTestId="create-farm-add-shape-map" iconName="klim/plus" size="xl" />
                    <span className={`${locationError ? "red-text " : ""}message`}>{t("fieldDetails.setSizeMessage")}</span>
                  </Card>
                  {locationError && <p className="red-text margin-top-25">{t("fieldDetails.shapeMandatoryDescription")}</p>}
                </>
              )}
            </section>
            <section className="padded-top">
              <Row gutter={0}>
                <Col>
                  <h2>{t("fieldDetails.headlineInformation")}</h2>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Input
                    dataTestId="create-farm-field-name"
                    className={nameError ? "ant-form-item-has-error" : ""}
                    label={t("fieldDetails.name")}
                    initialValue={field?.name}
                    name="name"
                    onChange={(v) => (v.target.value.length || field?.name?.length ? setNameError(false) : setNameError(true))}
                    rules={[{ required: true }]}
                  />
                  {nameError && <p className="red-text margin-0">{t("fieldDetails.mandatoryField")}</p>}
                </Col>
              </Row>
            </section>
            <FieldLocation
              farm={farm}
              field={field}
              fieldSize={fieldSize}
              parentFieldShape={fieldShape}
              parentLocation={location}
              onClose={() => setShowMapDrawer(false)}
              setFieldSize={setFieldSize}
              setParentFieldShape={setFieldShape}
              setParentLocation={(v: Coordinates | null) => setCurrentLocation(v)}
              showMapDrawer={showMapDrawer}
              showLocationMessage={() => showLocationMessage()}
            />
            {(fieldId || review) && (
              <section className="padded-top">
                <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]}>
                  <Col>
                    <h2 className="margin-bottom-100">{t("fieldDetails.deleteField")}</h2>
                    {canDeleteField({
                      carbonProgramResult: fieldId ? carbonProgramOutcomes?.[fieldId] : undefined,
                      carbonProgram,
                    }) ? (
                      <div>
                        <p>{t("fieldDetails.deleteText")}</p>
                        <p className="paragraph-m-bold red-text pointer" onClick={handleDeleteConfirmation}>
                          {t("fieldDetails.deleteButton")}
                        </p>
                      </div>
                    ) : (
                      <p>{t("fieldDetails.deleteFieldWithMethodsText")}</p>
                    )}
                  </Col>
                </Row>
              </section>
            )}
            {showFieldSplitDrawer && fieldShape?.geometry && field && (
              <SplitFieldDrawer field={field} geometry={fieldShape} onClose={() => setShowFieldSplitDrawer(false)} visible={showFieldSplitDrawer} />
            )}
            <StickyFooterButtons absolute handleBack={() => setShowFieldEditDrawer?.(false)}>
              <Button
                dataTestId="create-farm-field-save-button"
                type="primary"
                htmlType="submit"
                block
                disabled={loading || saving}
                loading={loading || saving}
              >
                {t("fieldDetails.save")}
              </Button>
            </StickyFooterButtons>
          </Form>
        ) : (
          <PageSpin />
        )}
      </section>
    </section>
  ) : null;
};
